import { ReactElement } from 'react';
import storeConnector from '@store/storeConnector';
import useGetInternalWidgetConfig from '@src/hooks/api/queries/useGetInternalWidgetConfig';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import useSubscription from '@src/hooks/useSubscription';
import { SiteMeta } from '@src/types/SiteMeta';
import { DataPointWithValue } from '@src/types/DataPoint';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import ArrowUp from '../components/ArrowUp/ArrowUp';
import MultiSwitchConnector from '../components/MultiSwitchConnector/MultiSwitchConnector';
import { Resistor } from '../components/Resistor/Resistor';
import ArrowDown from '../components/ArrowDown/ArrowDown';
import BreakerBasic from '../components/Breaker/BreakerBasic';
import { Henrietta_Device_Points_Config } from './henriettaDevicePointsConfig';
import LableValueRender from '../components/LableValueRender';
import { getDataPointCollectionByPointNames } from '../utils';

type HenriettaSLDProps = {
  siteMeta: SiteMeta;
};

function Henrietta_Diagram(props: HenriettaSLDProps): ReactElement {
  const { siteMeta } = props;

  const { data: rtacWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'henrietta_sld_rtac',
      {
        sourceDeviceId: siteMeta.siteId,
      },
      { enabled: true },
    );

  const rtacDataPointsWithValue = useSubscription(
    rtacWidgetConfig?.dataPoints || [],
  );

  let cbG1DataPoints: DataPointWithValue[] | undefined;
  let cbG2DataPoints: DataPointWithValue[] | undefined;
  let a1DataPoints: DataPointWithValue[] | undefined;
  let a2DataPoints: DataPointWithValue[] | undefined;
  let f1_1DataPoints: DataPointWithValue[] | undefined;
  let f1_2DataPoints: DataPointWithValue[] | undefined;
  let f1_3DataPoints: DataPointWithValue[] | undefined;
  let f2_1DataPoints: DataPointWithValue[] | undefined;
  let f2_2DataPoints: DataPointWithValue[] | undefined;
  let f2_3DataPoints: DataPointWithValue[] | undefined;

  if (rtacDataPointsWithValue.length > 0) {
    cbG1DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.CB_G1_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    cbG2DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.CB_G2_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    a1DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.A1_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    a2DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.A2_DATA_POINTS.map(
        (point) => point.pointName,
      ),
    );

    f1_1DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.F1_1_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f1_2DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.F1_2_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f1_3DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.F1_3_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f2_1DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.F2_1_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f2_2DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.F2_2_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );

    f2_3DataPoints = getDataPointCollectionByPointNames(
      rtacDataPointsWithValue,
      Henrietta_Device_Points_Config.F2_3_DATA_POINTS.map((point) =>
        point.pointName.toLowerCase(),
      ),
    );
  }

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='200 40 1200 700'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Henrietta SLD</title>
      <text
        x={720}
        y={60}
        fontSize={8}
        fill='white'
        stroke='white'
        strokeWidth={0.2}
      >
        70KV TO PG&E HENRIETTA
      </text>
      <text
        x={750}
        y={70}
        fontSize={8}
        fill='white'
        stroke='white'
        strokeWidth={0.2}
      >
        SUBSTATION
      </text>

      {/* up arrow */}
      <ArrowUp xCoordinate={777.5} yCoordinate={100} />

      <VerticalLine
        translateXCoordinate={780}
        translateYCoordinate={100}
        length={70}
        color='white'
        width={0.5}
      />

      <MultiSwitchConnector
        translateXCoordinate={300}
        translateYCoordinate={-375}
        horizontalLength={700}
        verticalLength={50}
        startingXCoordinate={140}
      />

      {/* ****** Left side resitor-breakers start ****** */}
      <g transform='translate(-200 -50)'>
        <g transform='rotate(90 575 840) translate(0 0)'>
          <Resistor label={'GSU T1'} />
        </g>

        <VerticalLine
          translateXCoordinate={640}
          translateYCoordinate={290}
          length={27}
          color='white'
          width={0.5}
        />

        {/* left-top breaker - breaker 1 */}
        <BreakerBasic
          labels={[
            { label: 'CB', xCoordinate: 50, yCoordinate: 10 },
            { label: '52G1', xCoordinate: 45, yCoordinate: 25 },
          ]}
          translateXCoordinate={625}
          translateYCoordinate={315}
          dataPointsWithValues={cbG1DataPoints}
        />

        <LableValueRender
          xPosition={730}
          yPosition={270}
          dataPoints={cbG1DataPoints}
          pointNames={Henrietta_Device_Points_Config.CB_G1_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={640}
          translateYCoordinate={342}
          length={25}
          color='white'
          width={0.5}
        />

        <MultiSwitchConnector
          translateXCoordinate={360}
          translateYCoordinate={-175}
          horizontalLength={300}
          verticalLength={50}
          startingXCoordinate={140}
        />

        {/* left-bottom-left breaker - breaker 2 */}
        <BreakerBasic
          labels={[{ label: '52A1', xCoordinate: 35, yCoordinate: 10 }]}
          translateXCoordinate={485}
          translateYCoordinate={420}
          dataPointsWithValues={a1DataPoints}
        />

        <LableValueRender
          xPosition={550}
          yPosition={400}
          dataPoints={a1DataPoints}
          pointNames={Henrietta_Device_Points_Config.A1_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={500}
          translateYCoordinate={450}
          length={140}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={498} yCoordinate={585} />

        {/* left-bottom-right breaker - breaker 3 */}
        <BreakerBasic
          labels={[{ label: '52M1', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={787}
          translateYCoordinate={420}
          defaultHot={false}
        />

        <VerticalLine
          translateXCoordinate={800}
          translateYCoordinate={450}
          length={25}
          color='white'
          width={0.5}
        />

        <MultiSwitchConnector
          translateXCoordinate={510}
          translateYCoordinate={-70}
          horizontalLength={300}
          verticalLength={50}
          startingXCoordinate={140}
        />

        <VerticalLine
          translateXCoordinate={800}
          translateYCoordinate={477}
          length={47}
          color='white'
          width={0.5}
        />

        {/* left breaker - breaker 4 */}
        <BreakerBasic
          labels={[{ label: '52F1-1', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={637}
          translateYCoordinate={527}
          dataPointsWithValues={f1_1DataPoints}
        />

        <LableValueRender
          xPosition={590}
          yPosition={600}
          dataPoints={f1_1DataPoints}
          pointNames={Henrietta_Device_Points_Config.F1_1_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={650}
          translateYCoordinate={555}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={647.5} yCoordinate={580} />

        {/* middle breaker - breaker 5 */}
        <BreakerBasic
          labels={[{ label: '52F1-2', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={788}
          translateYCoordinate={527}
          dataPointsWithValues={f1_2DataPoints}
        />

        <LableValueRender
          xPosition={740}
          yPosition={600}
          dataPoints={f1_2DataPoints}
          pointNames={Henrietta_Device_Points_Config.F1_2_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={800}
          translateYCoordinate={555}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={797.5} yCoordinate={580} />

        {/* right breaker - breaker 6 */}
        <BreakerBasic
          labels={[{ label: '52F1-3', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={937}
          translateYCoordinate={527}
          dataPointsWithValues={f1_3DataPoints}
        />

        <LableValueRender
          xPosition={890}
          yPosition={600}
          dataPoints={f1_3DataPoints}
          pointNames={Henrietta_Device_Points_Config.F1_3_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={950}
          translateYCoordinate={555}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={947.5} yCoordinate={580} />
      </g>
      {/* ****** Left side resitor-breakers end ****** */}

      {/* ****** Right side resitor-breakers start ****** */}
      <g transform='translate(200 -50)'>
        <VerticalLine
          translateXCoordinate={940}
          translateYCoordinate={290}
          length={27}
          color='white'
          width={0.5}
        />

        <g transform='rotate(90 725 990) translate(0 0)'>
          <Resistor label={'GSU T2'} />
        </g>

        {/* top breaker - breaker 1 */}
        <BreakerBasic
          labels={[
            { label: 'CB', xCoordinate: 45, yCoordinate: 10 },
            { label: '52G2', xCoordinate: 40, yCoordinate: 25 },
          ]}
          translateXCoordinate={927}
          translateYCoordinate={315}
          dataPointsWithValues={cbG2DataPoints}
        />

        <LableValueRender
          xPosition={820}
          yPosition={270}
          dataPoints={cbG2DataPoints}
          pointNames={Henrietta_Device_Points_Config.CB_G2_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={940}
          translateYCoordinate={342}
          length={25}
          color='white'
          width={0.5}
        />

        {/* top multi-switch connector */}
        <MultiSwitchConnector
          translateXCoordinate={650}
          translateYCoordinate={-175}
          horizontalLength={300}
          verticalLength={50}
          startingXCoordinate={140}
        />

        {/* right-bottom-left breaker - breaker 2 */}
        <BreakerBasic
          labels={[{ label: '52M2', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={775}
          translateYCoordinate={420}
          defaultHot={false}
        />

        <VerticalLine
          translateXCoordinate={790}
          translateYCoordinate={450}
          length={25}
          color='white'
          width={0.5}
        />

        {/* right-bottom-right breaker - breaker 3 */}
        <BreakerBasic
          labels={[{ label: '52A2', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={1077}
          translateYCoordinate={420}
          dataPointsWithValues={a2DataPoints}
        />

        <LableValueRender
          xPosition={970}
          yPosition={395}
          dataPoints={a2DataPoints}
          pointNames={Henrietta_Device_Points_Config.A2_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={1090}
          translateYCoordinate={450}
          length={135}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={1087.5} yCoordinate={580} />

        {/* right-bottom-multi-switch-for-children section start */}
        <MultiSwitchConnector
          translateXCoordinate={500}
          translateYCoordinate={-70}
          horizontalLength={300}
          verticalLength={50}
          startingXCoordinate={140}
        />

        <VerticalLine
          translateXCoordinate={790}
          translateYCoordinate={477}
          length={47}
          color='white'
          width={0.5}
        />
        {/* right-bottom-multi-switch-for-children section end */}

        {/* left-bottom-left breaker - breaker 4 */}
        <BreakerBasic
          labels={[{ label: '52F2-1', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={625}
          translateYCoordinate={527}
          dataPointsWithValues={f2_1DataPoints}
        />

        <LableValueRender
          xPosition={635}
          yPosition={600}
          dataPoints={f2_1DataPoints}
          pointNames={Henrietta_Device_Points_Config.F2_1_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={640}
          translateYCoordinate={555}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={637.5} yCoordinate={580} />

        {/* middle breaker - breaker 5 */}
        <BreakerBasic
          labels={[{ label: '52F2-2', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={775}
          translateYCoordinate={527}
          dataPointsWithValues={f2_2DataPoints}
        />

        <LableValueRender
          xPosition={785}
          yPosition={600}
          dataPoints={f2_2DataPoints}
          pointNames={Henrietta_Device_Points_Config.F2_2_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={790}
          translateYCoordinate={555}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={787.5} yCoordinate={580} />

        {/* right breaker - breaker 6 */}
        <BreakerBasic
          labels={[{ label: '52F2-3', xCoordinate: 35, yCoordinate: 15 }]}
          translateXCoordinate={927}
          translateYCoordinate={527}
          dataPointsWithValues={f2_3DataPoints}
        />

        <LableValueRender
          xPosition={935}
          yPosition={600}
          dataPoints={f2_3DataPoints}
          pointNames={Henrietta_Device_Points_Config.F2_3_DATA_POINTS}
        />

        <VerticalLine
          translateXCoordinate={940}
          translateYCoordinate={555}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={937.5} yCoordinate={580} />
      </g>
      {/* ****** Right side resitor-breakers end ******  */}
    </svg>
  );
}

export default storeConnector(Henrietta_Diagram, {
  config: ['siteMeta'],
});
